/**
 * @file Assemblage d'entites a partir des donnees : cibles (TargetDef -> composants) et chats (slot d'equipement -> composant Cat).
 */
import { Decimal } from '@shared/bignum';
import type { Uid } from '@shared/types';
import type { TargetDef } from '@config/types';
import { stageHp, stageMoney } from '../balance/formulas';
import type { SimContext } from '../Context';
import type { With } from './World';

export interface SpawnTargetOptions {
  /** Valeur de stage servant a scaler PV et argent (Main : stageValue courant ; autres modes : leur palier). */
  stageValue: number;
  hpMult?: number;
  rewardMult?: number;
  isBoss?: boolean;
  variant?: number;
  /** PV absolus (surcharge la formule ; ex. barriere, boss de raid). */
  hpOverride?: Decimal;
}

const PRIORITY: Record<TargetDef['family'], number> = { boss: 3, monster: 2, ore: 1, rock: 1, tree: 1, decor: 0 };

/**
 * Cree une cible. Le flag `shootable` est resolu ICI : TargetDef.shootable, puis surcharge du mode (rules.shootableOverride).
 * La cible est inseree dans la grille spatiale si elle a un collider (meme non tirable : collisions de decor solide).
 */
export const spawnTarget = (ctx: SimContext, def: TargetDef, x: number, y: number, o: SpawnTargetOptions): With<'target' | 'health' | 'collider' | 'reward'> => {
  const e = ctx.rt.world.create('target', x, y);
  const rules = ctx.modes.rules;
  const override = rules.shootableOverride?.[def.family];
  const shootable = override ?? def.shootable;
  const hp = o.hpOverride ?? stageHp(ctx.config.balance, o.stageValue).times(def.hpMult * (o.hpMult ?? 1));
  const harvestMode = rules.killRewards === 'resources' && def.harvestable && def.harvest !== undefined;
  e.health = { hp, max: hp, invulnerable: 0, hitsLeft: harvestMode ? def.harvest?.hits : undefined };
  e.reward = {
    money: def.rewardMult > 0 ? stageMoney(ctx.config.balance, o.stageValue).times(def.rewardMult * (o.rewardMult ?? 1)) : new Decimal(0),
    drops: def.drops,
    score: Math.max(1, Math.round(def.hpMult * 10)),
  };
  e.collider = { radius: def.colliderRadius * def.visual.scale, solid: def.solid };
  e.target = {
    defId: def.id,
    family: def.family,
    shootable,
    harvestable: def.harvestable,
    destructible: def.destructible,
    priority: PRIORITY[def.family] + (def.rarity.tier === 'rare' || def.rarity.tier === 'epic' ? 0.5 : 0),
    variant: o.variant ?? ctx.rng.spawn.int(0, Math.max(0, def.visual.variants - 1)),
    isBoss: o.isBoss ?? false,
    barrierDamage: def.barrierDamage ?? 0,
  };
  e.visual = { defId: def.id, scale: def.visual.scale * (o.isBoss ? 1.6 : 1), hitFlash: 0, dying: 0 };
  e.transform.rot = ctx.rng.spawn.range(0, Math.PI * 2);
  if (def.mover) {
    e.mover = { kind: def.mover.kind, speed: def.mover.speed, vx: 0, vy: 0, anchorX: 0, anchorY: 0, radius: def.mover.radius ?? 0, pushX: 0, pushY: 0 };
  }
  if (def.ai) e.ai = { behavior: def.ai.behavior, state: 'init', timer: 0, params: { ...(def.ai.params ?? {}) } };
  ctx.rt.targetGrid.insert(e.id, x, y, e.collider.radius);
  ctx.bus.emit('TargetSpawned', { id: e.id, defId: def.id, family: def.family });
  return e as With<'target' | 'health' | 'collider' | 'reward'>;
};

/** Cree le chat d'un slot (le slot 0 est le chef controle par le joueur). */
export const spawnCat = (ctx: SimContext, slot: number, weaponUid: Uid | null, x: number, y: number): With<'cat'> => {
  const e = ctx.rt.world.create('cat', x, y);
  e.cat = {
    slot,
    weaponUid,
    isLeader: slot === 0,
    isHelper: false,
    aiState: slot === 0 ? 'idle' : 'follow',
    targetId: null,
    aimAngle: Math.PI / 2,
    cooldown: 0.3 + slot * 0.07, // decalage pour eviter des tirs parfaitement synchrones
    spreadDeg: 0,
    sinceShot: 99,
    moving: false,
    shotSeq: 0,
    skinId: ctx.data.skins.equipped,
  };
  e.collider = { radius: 0.55, solid: false };
  e.visual = { defId: 'cat', scale: 1, hitFlash: 0, dying: 0 };
  e.transform.rot = Math.PI / 2;
  return e as With<'cat'>;
};
